import { lazy, Suspense } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import Layout from "./components/Layout";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminRoute from "./components/AdminRoute";
import Home from "./pages/Home";
import ShopClosed from "./pages/ShopClosed";
import { useAuth } from "./context/AuthContext";
import { useShop } from "./context/ShopContext";

const Products = lazy(() => import("./pages/Products"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const Cart = lazy(() => import("./pages/Cart"));
const Checkout = lazy(() => import("./pages/Checkout"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const MyOrders = lazy(() => import("./pages/MyOrders"));
const OrderDetail = lazy(() => import("./pages/OrderDetail"));
const Profile = lazy(() => import("./pages/Profile"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminProducts = lazy(() => import("./pages/admin/AdminProducts"));
const AdminProductForm = lazy(() => import("./pages/admin/AdminProductForm"));
const AdminOrders = lazy(() => import("./pages/admin/AdminOrders"));
const AdminOrderDetail = lazy(() => import("./pages/admin/AdminOrderDetail"));
const AdminSettingsLayout = lazy(() => import("./pages/admin/settings/AdminSettingsLayout"));
const AdminSettingsShop = lazy(() => import("./pages/admin/settings/AdminSettingsShop"));
const AdminSettingsPayments = lazy(() => import("./pages/admin/settings/AdminSettingsPayments"));
const AdminSettingsCouriers = lazy(() => import("./pages/admin/settings/AdminSettingsCouriers"));
const AdminSettingsEbay = lazy(() => import("./pages/admin/settings/AdminSettingsEbay"));
const AdminEbayTemplate = lazy(() => import("./pages/admin/settings/AdminEbayTemplate"));
const AdminSettingsOpenAI = lazy(() => import("./pages/admin/settings/AdminSettingsOpenAI"));
const AdminSettingsFintyre = lazy(() => import("./pages/admin/settings/AdminSettingsFintyre"));
const AdminQuotes = lazy(() => import("./pages/admin/AdminQuotes"));
const AdminQuoteNew = lazy(() => import("./pages/admin/AdminQuoteNew"));
const AdminQuoteDetail = lazy(() => import("./pages/admin/AdminQuoteDetail"));

function PageFallback() {
  return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <div className="w-8 h-8 border-4 border-pg-red border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function ShopGate({ children }) {
  const { salesEnabled, ready } = useShop();
  if (!ready) return <PageFallback />;
  if (!salesEnabled) return <ShopClosed />;
  return children;
}

function CheckoutRoute() {
  const { salesEnabled, ready } = useShop();
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  if (!ready || loading) return <PageFallback />;
  if (!salesEnabled) return <ShopClosed />;
  if (!isAuthenticated) return <Navigate to="/login" state={{ from: location }} replace />;
  return <Checkout />;
}

function App() {
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route element={<Layout />}>
          <Route path="/" element={<Home />} />
          <Route path="/prodotti" element={<ShopGate><Products /></ShopGate>} />
          <Route path="/prodotti/:slugOrId" element={<ShopGate><ProductDetail /></ShopGate>} />
          <Route path="/carrello" element={<ShopGate><Cart /></ShopGate>} />
          <Route path="/checkout" element={<CheckoutRoute />} />
          <Route path="/login" element={<Login />} />
          <Route path="/registrati" element={<Register />} />
          <Route element={<ProtectedRoute />}>
            <Route path="/ordini" element={<MyOrders />} />
            <Route path="/ordini/:id" element={<OrderDetail />} />
            <Route path="/profilo" element={<Profile />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Route>
        <Route element={<AdminRoute />}>
          <Route element={<AdminLayout />}>
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/prodotti" element={<AdminProducts />} />
            <Route path="/admin/prodotti/nuovo" element={<AdminProductForm />} />
            <Route path="/admin/prodotti/:id" element={<AdminProductForm />} />
            <Route path="/admin/ordini" element={<AdminOrders />} />
            <Route path="/admin/ordini/:id" element={<AdminOrderDetail />} />
            <Route path="/admin/preventivi" element={<AdminQuotes />} />
            <Route path="/admin/preventivi/nuovo" element={<AdminQuoteNew />} />
            <Route path="/admin/preventivi/:id" element={<AdminQuoteDetail />} />
            <Route element={<AdminSettingsLayout />}>
              <Route path="/admin/impostazioni" element={<Navigate to="/admin/impostazioni/vendita" replace />} />
              <Route path="/admin/impostazioni/vendita" element={<AdminSettingsShop />} />
              <Route path="/admin/impostazioni/pagamenti" element={<AdminSettingsPayments />} />
              <Route path="/admin/impostazioni/corrieri" element={<AdminSettingsCouriers />} />
              <Route path="/admin/impostazioni/ebay" element={<AdminSettingsEbay />} />
              <Route path="/admin/impostazioni/ebay/template" element={<AdminEbayTemplate />} />
              <Route path="/admin/impostazioni/openai" element={<AdminSettingsOpenAI />} />
              <Route path="/admin/impostazioni/fintyre" element={<AdminSettingsFintyre />} />
            </Route>
          </Route>
        </Route>
      </Routes>
    </Suspense>
  );
}

export default App;
